import React, { useCallback, useEffect, useState } from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import "./projects.css";
import { usePermissions } from "../../hooks/usePermissions";
import { blockDemoSave } from "../../helpers/guestMode";
import {
  addProjectLocation,
  formatTargetCompletion,
  loadProjectStructure,
} from "../../helpers/projectStructure";
import { safeUserErrorMessage } from "../../helpers/safeErrors";
import {
  WorkspaceAddButton,
  WorkspaceEmpty,
} from "../workspace/WorkspaceChrome";
import { WorkspaceNameSheet } from "../workspace/WorkspaceNameSheet";

/**
 * Location rows for the Project home. Same list, add row, and empty state
 * as the Estimate home. Persistence stays on project_locations.
 */
export function ProjectLocationsSection({
  projectId,
  editable,
  onOpenLocation,
  locations: providedLocations,
}) {
  const provided = Array.isArray(providedLocations);
  const { actor } = usePermissions();
  const [locations, setLocations] = useState(provided ? providedLocations : []);
  const [loading, setLoading] = useState(!provided);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const reload = useCallback(async () => {
    if (provided || !projectId) return;
    setLoading(true);
    setError("");
    try {
      setLocations(await loadProjectStructure(actor, projectId));
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to load project locations."));
    } finally {
      setLoading(false);
    }
  }, [actor, projectId, provided]);

  useEffect(() => {
    if (provided) {
      setLocations(providedLocations);
      setLoading(false);
      return;
    }
    reload();
  }, [provided, providedLocations, reload]);

  return (
    <>
      <div className="pj-label">Locations</div>
      {error && <div className="est-inline-error">{error}</div>}
      {loading && <WorkspaceEmpty>Loading locations…</WorkspaceEmpty>}

      {!loading && locations.length === 0 && (
        editable ? (
          <WorkspaceAddButton first onClick={() => setShowAdd(true)}>
            + Add First Location
          </WorkspaceAddButton>
        ) : (
          <WorkspaceEmpty>No locations yet.</WorkspaceEmpty>
        )
      )}

      {!loading && locations.length > 0 && (
        <>
          <div className="pj-list">
            {locations.map((location) => {
              const count = (location.features || []).length;
              return (
                <button
                  type="button"
                  key={location.project_location_id}
                  className="pj-navrow"
                  onClick={() => onOpenLocation(location.project_location_id)}
                >
                  <span className="pj-navrow__text">
                    <span className="pj-navrow__title">{location.name}</span>
                    <span className="pj-navrow__sub">
                      {`${count} feature${count === 1 ? "" : "s"} · ${
                        formatTargetCompletion(location.target_completion_date)
                          ? `Target ${formatTargetCompletion(location.target_completion_date)}`
                          : "No target date"
                      }`}
                    </span>
                  </span>
                  <RecIcon name="chevron" className="pj-navrow__chev" />
                </button>
              );
            })}
          </div>
          {editable && (
            <WorkspaceAddButton onClick={() => setShowAdd(true)}>+ Add Location</WorkspaceAddButton>
          )}
        </>
      )}

      {showAdd && (
        <WorkspaceNameSheet
          title="Add Location"
          fieldLabel="Location Name"
          placeholder="Bathroom"
          primaryLabel="Add Location"
          withTargetCompletion
          onClose={() => setShowAdd(false)}
          onSubmit={async (name, options) => {
            if (blockDemoSave(actor)) {
              throw new Error("Demo Mode — Sign in to save your work.");
            }
            await addProjectLocation(actor, projectId, name, options);
            await reload();
          }}
        />
      )}
    </>
  );
}
